import React, { useState } from "react";

import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useClose } from "@headlessui/react";
import { twJoin } from "tailwind-merge";

import { DecisionEnvironment } from "src/api/types";
import { CustomPopover } from "src/base-components/Popover";
import { FallbackInput } from "src/cases/common/FallbackInput";
import { ResourceSearchPane } from "src/datasets/components/ResourceSearchPane";
import { IconButton } from "src/design-system/Icon";
import { FIELD_BORDER } from "src/design-system/utils/fieldStyles";
import { LinkedEntityPill } from "src/entities/entityView/LinkedEntityPill";
import { useEntitySearch } from "src/entities/useEntitySearch";
import { useEnvironment } from "src/hooks/useEnvironment";
import { ErrorBoundary } from "src/utils/ErrorBoundary";

type SearchableEntityRefInputProps = {
  entityType: string;
  value: string;
  onChange: (id: string) => void;
  errored: boolean;
};

export const SearchableEntityRefInput: React.FC<
  SearchableEntityRefInputProps
> = (props) => (
  <ErrorBoundary
    fallbackErrorContent={
      <FallbackInput
        placeholder="Search failed, please enter Entity ID manually"
        onCommit={props.onChange}
      />
    }
  >
    <SearchableEntityRefInputInner {...props} />
  </ErrorBoundary>
);

const SearchableEntityRefInputInner: React.FC<
  SearchableEntityRefInputProps
> = ({ entityType, value, onChange, errored }) => {
  const [pageEnv] = useEnvironment();
  const env = pageEnv as DecisionEnvironment;
  const [searchQuery, setSearchQuery] = useState("");

  const {
    items,
    isLoading,
    displayName,
    primaryPropertyDisplayName,
    selectedDisplayValue,
    entityIcon,
  } = useEntitySearch({
    entityType,
    searchQuery,
    selectedValue: value,
  });

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <CustomPopover
      button={
        <div
          className={twJoin(
            "flex h-8 w-full items-center rounded-lg border bg-white px-3 text-left font-inter-normal-12px focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
            !errored && FIELD_BORDER.default,
            errored && FIELD_BORDER.errored,
          )}
          data-loc="entity-ref-value"
          tabIndex={0}
        >
          {value ? (
            <div className="flex w-full items-center gap-1">
              <span onClick={(e) => e.stopPropagation()}>
                <LinkedEntityPill
                  entityIcon={entityIcon}
                  environment={env}
                  fullWidth={false}
                  id={value}
                  name={selectedDisplayValue || value}
                  schemaId={entityType}
                />
              </span>
              <div className="ml-auto">
                <IconButton icon={faTimes} size="xs" onClick={handleClear} />
              </div>
            </div>
          ) : (
            <span className="text-gray-500">Search Entity</span>
          )}
        </div>
      }
      placement="bottom-start"
      wrapperClassName="w-full"
    >
      <EntitySearchPaneContent
        displayName={displayName}
        env={env}
        isLoading={isLoading}
        items={items}
        primaryPropertyDisplayName={primaryPropertyDisplayName}
        searchQuery={searchQuery}
        onChange={onChange}
        onSearchQueryChange={setSearchQuery}
      />
    </CustomPopover>
  );
};

const EntitySearchPaneContent: React.FC<{
  env: DecisionEnvironment;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  isLoading: boolean;
  items: { label: string; key: string; isFetching: boolean }[];
  displayName: string;
  primaryPropertyDisplayName: string | undefined;
  onChange: (id: string) => void;
}> = ({
  env,
  searchQuery,
  onSearchQueryChange,
  isLoading,
  items,
  displayName,
  primaryPropertyDisplayName,
  onChange,
}) => {
  const close = useClose();

  return (
    <div className="w-96">
      <ResourceSearchPane
        filters={{
          environment: env,
          searchQuery,
        }}
        hideEnvironmentFilter
        isLoading={isLoading}
        items={items}
        resourceName={displayName}
        searchPlaceholder={
          primaryPropertyDisplayName
            ? `Search by ID or ${primaryPropertyDisplayName}`
            : "Search by ID"
        }
        onChangeFilters={(filters) => {
          onSearchQueryChange(filters.searchQuery);
        }}
        onSelect={(item) => {
          onChange(item.key);
          close();
        }}
      />
    </div>
  );
};
